/**
 * Node data for the satisfaction and scoring nodes — the passive scorer, the
 * active prompt/parse pair, and the generic score recorder.
 */

import { DbEnum } from "../../../generated/prisma-enums";
import type { BaseNodeData } from "./workflow-node-base.types";

/**
 * Configuration for the AI Passive Satisfaction Score node.
 *
 * Reads a conversation, runs the operator-authored analysis prompt,
 * extracts a numeric score in the configured framework's range, and
 * writes a `SatisfactionScore` row via `RecordSatisfactionScoreUseCase`
 * (which emits `satisfaction.score.upserted` for downstream rules).
 *
 * `instructions` (inherited from `BaseNodeData`) holds the analysis
 * prompt template — it is fed to the LLM with the rendered
 * conversation transcript appended so the model can score from the
 * actual chat content rather than relying on the operator stuffing
 * the transcript into the prompt manually.
 *
 * `chatIdSource` is a variable reference (e.g.
 * `{{trigger.conversation.id}}` or `{{previous.chatId}}`) resolved
 * against the workflow execution context at runtime. The conversation
 * the score attaches to is whichever chat that resolves to.
 *
 * The score itself attaches to the chat's most recent non-deleted
 * message at execution time — that's the "AI's opinion as of this
 * snapshot" semantic. Subsequent runs against the same chat after
 * new messages land create new score rows; runs while the chat is
 * static refresh the existing row (messageId @unique on
 * `SatisfactionScore`).
 */
export type AIPassiveSatisfactionScoreNodeData = BaseNodeData & {
  /** Which framework's range the analysis prompt is expected to produce. */
  framework: DbEnum.SatisfactionFramework;
  /** Workflow-context variable reference resolving to a chat ID. */
  chatIdSource: string;
};

/**
 * What entity a Record Score node attaches its score to.
 *
 * - `conversation` → the chat resolved from `chatIdSource` (ScorableType.CHAT).
 *   The high-value default: scores land on the conversation the flow is acting
 *   on, so conversation analytics and the "criterion scored below X" rules see
 *   them.
 * - `workflowRun` → the parent workflow execution (ScorableType.WORKFLOW_EXECUTION),
 *   auto-resolved from the execution context. "This run scored X" with no
 *   variable wiring required.
 */
export const RECORD_SCORE_TARGETS = ["conversation", "workflowRun"] as const;
export type RecordScoreTarget = (typeof RECORD_SCORE_TARGETS)[number];

/**
 * Configuration for the Record Score node.
 *
 * A builder drops this node into a flow to write a universal `Score`
 * (Analytics v2 L2) from data available at that point in the execution. It is
 * the generic, emitter-agnostic complement to the AI satisfaction nodes: no LLM,
 * no framework — it records a value the flow already has.
 *
 * The value is resolved from `valueSource` (a variable reference like a mention
 * span, or a typed literal) and coerced to the shape `valueType` demands — a
 * finite number for NUMERIC, a non-empty string for CATEGORICAL, a boolean for
 * BOOLEAN. Coercion failures throw at execution rather than silently recording a
 * junk score. The value is NEVER auto-sniffed from prior output — the builder
 * points at it explicitly.
 *
 * `chatIdSource` is a variable reference resolving to a chat id, used only when
 * `scorableTarget` is `conversation` (identical mechanism to the passive
 * satisfaction node). For `workflowRun` the scorable id comes from the execution
 * context and `chatIdSource` is ignored.
 *
 * The score is emitted as `ScoreEmitterType.WORKFLOW_NODE`; the node id and
 * workflow/execution ids are recorded in the score metadata for attribution.
 */
export type RecordScoreNodeData = BaseNodeData & {
  /** Metric key recorded as `Score.name`, e.g. "resolution_quality". */
  metricName: string;
  /** Which value shape this node records (NUMERIC / CATEGORICAL / BOOLEAN). */
  valueType: DbEnum.ScoreValueType;
  /** Variable reference or literal resolving to the score value; coerced per `valueType`. */
  valueSource: string;
  /** What the score attaches to. */
  scorableTarget: RecordScoreTarget;
  /** Variable reference resolving to a chat id — used only when `scorableTarget` is `conversation`. */
  chatIdSource: string;
  /** Optional variable reference or literal for the score rationale. */
  reasoningSource: string;
};

/**
 * Send Satisfaction Prompt node data.
 *
 * Active CSAT/NPS message dispatch — the operator-driven complement
 * to the AI Passive Satisfaction Score node. Reads a chat by
 * `chatIdSource`, sends the templated `instructions` body on the
 * chat's existing channel (WhatsApp / Slack / Email / Teams / etc.)
 * via the chat's `deploymentSession`, then returns the persisted
 * message id.
 *
 * The companion `parseSatisfactionReply` node (shipped alongside)
 * listens on `conversation.message_received` and extracts the
 * customer's numeric reply against the same `framework`. Pairing
 * is by chat history — no sibling state row is written by this
 * node (Variant A): the latest outbound message in the thread is
 * the implicit "awaiting reply" marker and the operator filters
 * the parse node by deployment / agent / channel.
 *
 * `instructions` (inherited from `BaseNodeData`) is the message
 * body sent to the customer. Variable refs (`{{trigger.*}}`,
 * `{{previous.*}}`) are resolved against the workflow execution
 * context at runtime so the prompt can quote chat or trigger
 * fields back to the customer.
 */
export type SendSatisfactionPromptNodeData = BaseNodeData & {
  /** Which framework the customer is being prompted on. */
  framework: DbEnum.SatisfactionFramework;
  /** Workflow-context variable reference resolving to a chat ID. */
  chatIdSource: string;
  /**
   * Twilio Content SID of an approved WhatsApp template. When set, the
   * executor fetches the template at run time and dispatches via the
   * Twilio Content API instead of the plain-text path — required for
   * WhatsApp deployments outside the 24-hour customer-care window. The
   * materialiser populates this from the preset's `approvedTemplate`
   * prompt; non-WhatsApp deployments leave it undefined.
   */
  templateId?: string;
  /**
   * Variable map interpolated into the WhatsApp template body. Keys
   * match the template's `{{n}}` placeholders. Empty object is valid
   * when the template has no variables. Used only when `templateId`
   * is set.
   */
  templateParameters?: Record<string, string>;
};

/**
 * Parse Satisfaction Reply node data.
 *
 * The receiving end of an active CSAT/NPS prompt — runs on a
 * `conversation.message_received` trigger, reads the inbound
 * message body, asks an LLM to extract a numeric score in the
 * configured framework's range, and writes a `SatisfactionScore`
 * row via `RecordSatisfactionScoreUseCase`. The use case emits
 * `satisfaction.score.upserted` for downstream rules
 * (threshold-crossing detractor escalation, etc.).
 *
 * `instructions` (inherited from `BaseNodeData`) is the parse
 * prompt operators tune to handle their own copy ("how do I read
 * '5/5' vs '5 out of 10' vs 'thumbs up'") — the LLM gets the
 * inbound message body + this prompt and forces a single function
 * call returning `{ rawScore, confidence }`. The `confidence` is
 * a 0-1 float the operator can branch on downstream to filter
 * low-quality extractions before they hit dashboard analytics.
 *
 * `messageIdSource` resolves at runtime to the inbound message id
 * the score attaches to (typically `{{trigger.message.id}}`). The
 * use case writes one row keyed on `messageId` so re-running the
 * node on the same trigger refreshes the same row in place
 * (messageId @unique on `SatisfactionScore`).
 *
 * `chatIdSource` resolves to the chat the message belongs to —
 * threaded into the use case for analytics-context lookup.
 */
export type ParseSatisfactionReplyNodeData = BaseNodeData & {
  /** Which framework's range the parse prompt is expected to produce. */
  framework: DbEnum.SatisfactionFramework;
  /** Workflow-context variable reference resolving to a chat ID. */
  chatIdSource: string;
  /** Workflow-context variable reference resolving to the inbound message ID. */
  messageIdSource: string;
};
